import { ApiClient } from '@api/api-configuration';
import { NotificationType } from '@api/READ_ONLY/notifications_api/Api';
import { captureError } from '@app/lib/Sentry';
import { message } from 'antd';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DataExportCompletedNotificationType, DataExportFailedNotificationType } from '../types/notifications';

export type ExportNotification = DataExportCompletedNotificationType | DataExportFailedNotificationType;
export type ExportCardState = 'ready' | 'empty' | 'failed' | 'unavailable';
export type ExportDownloadOutcome = 'downloaded' | 'unavailable' | 'failed' | 'busy';

export const EXPORT_STATE_TAG: Record<ExportCardState, { color: string; labelKey: string }> = {
  ready: { color: 'success', labelKey: 'notifications-tag-export-ready' },
  empty: { color: 'default', labelKey: 'notifications-tag-export-empty' },
  failed: { color: 'error', labelKey: 'notifications-tag-export-failed' },
  unavailable: { color: 'default', labelKey: 'notifications-export-unavailable' },
};

const GONE = new Set([404, 410]);

/** File name from `Content-Disposition: attachment; filename*=UTF-8''<urlencoded>`. */
export const filenameFrom = (header?: string | null) => {
  const match = header?.match(/filename\*=UTF-8''([^;]+)/i);
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return undefined; // malformed percent-encoding
  }
};

export const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/** One download path for both export cards; legacy and expired exports are just unavailable. */
export const useExportDownload = (notification: ExportNotification) => {
  const { t } = useTranslation();
  const meta = notification.metadata?.[0];
  const [gone, setGone] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // A ref, not the state: two clicks in one tick both see downloading === false.
  const inFlight = useRef(false);

  const failed = notification.type === NotificationType.DataExportFailed || meta?.success === false;
  const exportId = meta?.exportId;
  let state: ExportCardState = 'ready';
  if (failed) state = 'failed';
  else if (exportId == null || gone) state = 'unavailable';
  else if (meta?.rowCount === 0) state = 'empty';
  const available = state === 'ready';
  const format = meta?.format ?? 'csv';

  const download = useCallback(async (): Promise<ExportDownloadOutcome> => {
    if (!available || exportId == null) return 'unavailable';
    if (inFlight.current) return 'busy';
    inFlight.current = true;
    setDownloading(true);
    try {
      const response = await ApiClient.visitsApi.request<Blob>({
        path: `/exports/${exportId}/download`,
        method: 'GET',
        format: 'blob',
      });
      const header = response.headers?.['content-disposition'] as string | undefined;
      saveBlob(response.data, filenameFrom(header) ?? `export.${format}`);
      message.success(t('notifications-download-success'));
      return 'downloaded';
    } catch (error) {
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      if (status !== undefined && GONE.has(status)) {
        setGone(true);
        return 'unavailable';
      }
      captureError(error as Error, { context: 'Export file download', exportId });
      message.error(t('notifications-download-error'));
      return 'failed';
    } finally {
      inFlight.current = false;
      setDownloading(false);
    }
  }, [available, exportId, format, t]);

  return { state, available, downloading, download };
};
